import type {
	CreateQuoteInput,
	NationalCloud,
	QuoteV2Rollout,
} from 'services/apollo/types/generated';

export const QUOTE_V2_EXPERIENCE_VIEWED_EVENT = 'QuoteV2.ExperienceViewed';

// These are supported classifier inputs, not the server-controlled rollout allowlist.
const QUOTE_V2_GOVERNMENT_PROGRAMS: readonly string[] = [
	'MCAFederalDirect',
	'GSA',
	'OBX',
	'RockCreek',
];
const QUOTE_V2_SUPPORTED_PROGRAMS: readonly string[] = [
	'MCA',
	'China21VCA',
	'UKCCF',
	...QUOTE_V2_GOVERNMENT_PROGRAMS,
];

export function isQuoteV2ExperienceEnabled(
	flightEnabled: boolean,
	rollout: QuoteV2Rollout | null | undefined
): boolean {
	return flightEnabled && (rollout == null || (rollout.enabled && rollout.userEligible));
}

// UX preview only. GQL repeats the decision against the constructed QS request.
export function isQuoteV2CreationAllowed(
	input: CreateQuoteInput,
	rollout: QuoteV2Rollout | null | undefined,
	defaultCloud?: NationalCloud
): boolean {
	// A successfully loaded legacy flight has no rollout policy.
	if (rollout == null) return true;
	if (!rollout?.enabled || !rollout.userEligible) return false;
	if (input.program && !QUOTE_V2_SUPPORTED_PROGRAMS.includes(input.program)) {
		return false;
	}
	const clouds = input.clouds ?? (defaultCloud ? [defaultCloud] : []);
	const agreementType = input.enrollmentNumberForLegacy ? 'legacy' : 'modern';
	const audience = input.audience ?? 'commercial';
	const motion = input.motion ?? 'Direct';
	const government =
		!!input.governmentEngagementType ||
		input.fgoe === true ||
		['government', 'CHK', 'ROCK', 'GSAASCEND', 'GSAMAS'].includes(audience) ||
		QUOTE_V2_GOVERNMENT_PROGRAMS.includes(input.program ?? '');
	const proposalType =
		agreementType === 'legacy'
			? 'Legacy'
			: government
			? 'Government'
			: input.program === 'UKCCF' || input.transactOnBehalf
			? 'NonStandard'
			: audience === 'commercial'
			? 'Commercial'
			: undefined;
	const channel =
		input.channelType === 'USMB'
			? 'USMB'
			: motion === 'Direct'
			? 'Direct'
			: motion === 'Indirect' || motion === 'Partner'
			? 'Indirect'
			: motion === 'PartnerReseller'
			? 'IndirectCSP'
			: undefined;
	return (
		!!proposalType &&
		!!channel &&
		clouds.length > 0 &&
		rollout.allowedContexts.some(
			rule =>
				rule.agreementTypes.includes(agreementType) &&
				rule.proposalTypes.includes(proposalType) &&
				rule.channels.includes(channel) &&
				clouds.every(cloud => rule.clouds.includes(cloud))
		)
	);
}
